import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth.js";
import { useNavigate } from "react-router-dom";
import { getUserById } from "../../models/UserFunctions.js";
import { getOrgByRef, getUsersByOrgRef } from "../../models/OrgFunctions.js";
import ConfirmationSmsModal from "../../modals/ConfirmationSmsModal.js";
import Column from "../../layout/Column.js";
import Row from "../../layout/Row.js";

import { Switch, useTheme } from "@mui/material";

import { useAppContext } from "../../config/AppContext.js";
import { set } from "date-fns";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import {
  getArrayDocDataByRef,
  getDateFromFlutter,
  getDateFromSeconds,
  getDocDataByRef,
} from "../../models/DataFunctions.js";
import { se } from "date-fns/locale";
import DropDownInline from "../../dropdowns/DropDownInline.js";
import BarChart1 from "../../charts/BarChart1.js";
import SmallSpinner from "../../loading/SmallSpinner.js";
import ListStatBlocks from "./ListStatBlocks.js";
import { useSnackbar } from "notistack";
import Tippy from "@tippyjs/react";
import { Spin } from "antd";
import Icon1 from "../../icons/Icon.js";
import { hasAudit, hasInfiltro } from "../../hexagone/VisibilityHelpers.js";
import { audit_steps_titles } from "../../folder_modals/TimelineFolderAudit.js";
import getTemporaryUrl from "../../api/getTemporaryUrl.js";
import JSZip from "jszip";
import useDashboardStatsStore from "../../stores/dashboardStatsStore.js";
import getDashboardStats from "../../api/database/getDashboardStats.js";
const MasterSmallStats = ({ navigateWorkspace, userData, orgData }) => {
  const theme = useTheme();
  const { currentUser, loading } = useAuth();

  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const { stats: cachedStats, setStats: setCachedStats } = useDashboardStatsStore();

  const [loaded, setLoaded] = useState(false);
  const [dict_stats, setDict_stats] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  // Fetch stats from backend — returns cached by default, recomputes if stale+force
  const handleFetchData = async (signal, forceRefresh = false) => {
    try {
      if (forceRefresh) setRefreshing(true);
      const stats = await getDashboardStats(prefix, orgData.id, {
        forceRefresh,
        signal,
      });
      if (signal?.aborted) return;
      setDict_stats(stats);
      setCachedStats(stats);
      setLoaded(true);

      // If server returned stale cache, silently refresh in background
      if (stats.stale && !forceRefresh) {
        handleFetchData(signal, true);
      } else {
        setRefreshing(false);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching data:", error);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    const controller = new AbortController();
    handleFetchData(controller.signal, true);
  };
  const handleGoRDVFolder = () => {
    let new_folders_confis = appState.folders_config;
    new_folders_confis.only_folder_filter = "rdv";
    setAppState((prevState) => ({
      ...prevState,
      folders_config: new_folders_confis,
    }));

    navigateWorkspace("/folders");
  };
  const handleGoMAJFolder = () => {
    let new_folders_confis = appState.folders_config;
    new_folders_confis.only_folder_filter = "modif";
    setAppState((prevState) => ({
      ...prevState,
      folders_config: new_folders_confis,
    }));

    navigateWorkspace("/folders");
  };
  const handleGoXMLFolder = () => {
    let new_folders_confis = appState.folders_config;
    new_folders_confis.only_folder_filter = "xml";
    setAppState((prevState) => ({
      ...prevState,
      folders_config: new_folders_confis,
    }));

    navigateWorkspace("/folders");
  };
  const handleGoLateFolder = () => {
    let new_folders_confis = appState.folders_config;
    new_folders_confis.only_folder_filter = "late";
    setAppState((prevState) => ({
      ...prevState,
      folders_config: new_folders_confis,
    }));

    navigateWorkspace("/folders");
  };

  const handleGoAuditConfigFolder = (value) => {
    let new_folders_confis = appState.folders_config;
    new_folders_confis.audit_folder_filter = audit_steps_titles[value];
    setAppState((prevState) => ({
      ...prevState,
      folders_config: new_folders_confis,
    }));

    navigateWorkspace("/folders");
  };

  useEffect(() => {
    if (orgData) {
      // Show cached stats immediately if available
      if (cachedStats) {
        setDict_stats(cachedStats);
        setLoaded(true);
      }
      // Fetch from server (returns cached quickly, auto-refreshes if stale)
      const controller = new AbortController();
      handleFetchData(controller.signal);
      return () => controller.abort();
    }
  }, [orgData]);

  // Display loading message while checking authentication or fetching user data
  // If the data is still loading, show skeletons
  // loading || !currentUser || !userData
  if (loading || !currentUser || !userData || !orgData) {
    return <></>;
  }

  const computedAt = dict_stats?.computed_at
    ? new Date(dict_stats.computed_at)
    : null;
  const minutesAgo = computedAt
    ? Math.round((Date.now() - computedAt.getTime()) / 60000)
    : null;

  return (
    <div className="flex flex-col w-fit h-fit  ">
      {loaded && (
        <div className="flex flex-row items-center gap-2 mt-2 text-xs text-gray-400">
          {minutesAgo !== null && (
            <span>
              Mis à jour il y a{" "}
              {minutesAgo < 1
                ? "moins d'une minute"
                : minutesAgo < 60
                  ? `${minutesAgo} min`
                  : `${Math.round(minutesAgo / 60)}h`}
            </span>
          )}
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 disabled:text-gray-300"
          >
            <span className={refreshing ? "animate-spin" : ""}>↻</span>
            {refreshing ? "Actualisation..." : "Actualiser"}
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 max-w-c1p4k mt-4">
        <BlockNumer
          icon="folder"
          fill="black"
          title="Dossiers"
          onClick={() => navigateWorkspace("/folders")}
          number={dict_stats.folders}
          shadow_color="shadow-indigo-100"
        />{" "}
        <BlockNumer
          icon="planning"
          fill="black"
          title="Evénements"
          onClick={() => navigateWorkspace("/planning")}
          number={dict_stats.events}
          shadow_color="shadow-indigo-100"
        />{" "}
        <BlockNumer
          icon="message"
          title="Messages non lus"
          onClick={() => navigateWorkspace("/messages")}
          number={dict_stats.notified_folders_count}
          shadow_color="shadow-indigo-100"
        />
        <BlockNumer
          icon="devis"
          title="Devis brouillons"
          onClick={() => navigateWorkspace("/invoices")}
          number={dict_stats.draft_devis}
          shadow_color="shadow-indigo-100"
        ></BlockNumer>
        <BlockNumer
          icon="devis"
          title="Devis non envoyés"
          onClick={() => navigateWorkspace("/invoices")}
          number={dict_stats.not_done_devis}
          shadow_color="shadow-indigo-100"
        ></BlockNumer>
        <BlockNumer
          icon="devis"
          title="Devis en attente"
          onClick={() => navigateWorkspace("/invoices")}
          number={dict_stats.waiting_devis}
          shadow_color="shadow-indigo-100"
        ></BlockNumer>
        <BlockNumer
          icon="devis"
          title="Devis signés prêts"
          onClick={() => navigateWorkspace("/invoices")}
          number={dict_stats.done_no_folder_devis}
          shadow_color="shadow-indigo-100"
        ></BlockNumer>
        {hasInfiltro(orgData) && (
          <>
            <BlockNumer
              icon="date"
              onClick={handleGoRDVFolder}
              title="Demandes de RDV"
              number={dict_stats.rdv_count}
              shadow_color="shadow-indigo-100"
            />{" "}
            <BlockNumer
              icon="xml"
              onClick={handleGoXMLFolder}
              title="Demandes de XML"
              number={dict_stats.xml_requested_count}
              shadow_color="shadow-indigo-100"
            />{" "}
            <BlockNumer
              icon="restart"
              onClick={handleGoMAJFolder}
              title="Demandes de MAJ"
              number={dict_stats.maj_requested_count}
              shadow_color="shadow-indigo-100"
            />
            <BlockNumer
              icon="late"
              onClick={handleGoLateFolder}
              title="Dossiers en retard"
              number={dict_stats.late_folders_count}
              shadow_color="shadow-indigo-100"
            />
          </>
        )}
        {hasAudit(orgData) && (
          <>
            <BlockNumer
              icon="horloge"
              fill="black"
              title="En prospection"
              number={dict_stats["En prospection"] || 0}
              onClick={() => handleGoAuditConfigFolder(0)}
            />
            <BlockNumer
              icon="search"
              onClick={() => handleGoAuditConfigFolder(1)}
              title="RDV Audit-MAR 1"
              number={dict_stats["RDV Audit-MAR 1"] || 0}
            />
            <BlockNumer
              icon="horloge"
              onClick={() => handleGoAuditConfigFolder(2)}
              title="Audit restitué"
              number={dict_stats["Audit restitué"] || 0}
            />
            <BlockNumer
              icon="control"
              onClick={() => handleGoAuditConfigFolder(3)}
              title="En contrôle conformité"
              number={dict_stats["En contrôle conformité"] || 0}
            />
            <BlockNumer
              icon="folder"
              onClick={() => handleGoAuditConfigFolder(4)}
              title="Déposé/ en instruction"
              number={dict_stats["Déposé/ en instruction"] || 0}
            />
            <BlockNumer
              icon="folder"
              onClick={() => handleGoAuditConfigFolder(5)}
              title="Dossiers octroyés"
              number={dict_stats["Dossier octroyé"] || 0}
            />{" "}
            <BlockNumer
              icon="folder"
              onClick={() => handleGoAuditConfigFolder(6)}
              title="Demande paiement"
              number={dict_stats["Demande paiement"] || 0}
            />
            <BlockNumer
              icon="horloge"
              onClick={() => handleGoAuditConfigFolder(7)}
              title="Visite MAR 2"
              number={dict_stats["Visite MAR 2"] || 0}
            />
            <BlockNumer
              icon="horloge"
              onClick={() => handleGoAuditConfigFolder(8)}
              title="Demande de solde"
              number={dict_stats["Demande de solde"] || 0}
            />
            <BlockNumer
              icon="done2"
              onClick={() => handleGoAuditConfigFolder(9)}
              title="Dossiers clôturés"
              number={dict_stats["Dossier clôturé"] || 0}
            />
          </>
        )}
        <div className="bg-white rounded-xl cursor-pointer gap-2 pt-4 hover:shadow-lg hover:shadow-indigo-100 p-4 flex flex-col shadow-md shadow-indigo-100 w-c200   border-blue-100">
          <div className={`  text-black flex flex-row  h-4 gap-2 items-center`}>
            <div className=" w-c20   ">
              <Icon1 icon_name={"bridge"} size={18} fill={"black"}></Icon1>
            </div>
            <div className=" text-h3    ">Bridge</div>
            <div
              className={` ml-auto w-2 h-2 ${orgData.bridge?.connected ? "bg-green-300" : "bg-gray-300"} rounded-full ml-2`}
            ></div>
          </div>
          {hasInfiltro(orgData) && (
            <div
              className={`  text-black flex flex-row  h-4 gap-2 items-center`}
            >
              <div className=" w-c20   ">
                <Icon1 icon_name={"rtb"} size={20} fill={"black"}></Icon1>
              </div>

              <div className=" text-h3    ">RT Batiment</div>
              <div
                className={`ml-auto w-2 h-2 ${orgData.rtb_connected ? "bg-green-300" : "bg-gray-300"} rounded-full ml-2`}
              ></div>
            </div>
          )}
          <div className={`  text-black flex flex-row  h-4 gap-2 items-center`}>
            <div className=" w-c20   ">
              <Icon1 icon_name={"obbc"} size={18} fill={"black"}></Icon1>
            </div>
            <div className=" text-h3    ">OBBC</div>
            <div
              className={`ml-auto w-2 h-2 ${orgData.obbc?.connected ? "bg-green-300" : "bg-gray-300"} rounded-full ml-2`}
            ></div>
          </div>
          <div className={`  text-black flex flex-row  h-4 gap-2 items-center`}>
            <div className=" w-c20   ">
              <Icon1 icon_name={"pennylane"} size={18} fill={"black"}></Icon1>
            </div>
            <div className=" text-h3    ">Pennylane</div>
            <div
              className={`ml-auto w-2 h-2 ${orgData.pennylane?.connected ? "bg-green-300" : "bg-gray-300"} rounded-full ml-2`}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterSmallStats;

export const BlockNumer = ({
  title,
  number = null,
  icon = "folder",
  fill = "black",
  textclassname = "",
  sufix = "",
  onClick = () => {},
  shadow_color = "shadow-indigo-100",
}) => {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl cursor-pointer  hover:shadow-lg  p-4 flex flex-col shadow-md ${shadow_color} hover:${shadow_color} w-c200   border-blue-100`}
    >
      <Icon1 icon_name={icon} size={20} fill={fill}></Icon1>{" "}
      <div
        className={` text-hx2 font-semibold text-black flex flex-row mt-4  ${textclassname}`}
      >
        {number !== null && !isNaN(number) ? number + sufix : <SmallSpinner />}
      </div>
      <div className=" text-h3 flex flex-row   ">{title}</div>
    </div>
  );
};
